import Navbar from "../../Navbar/NavbarComponent/Navbar";
import { useEffect, useMemo, useState } from "react";
import Table from "../../../shared/components/Table";
import {
  ACTION_LABEL,
  BUTTON_LABEL,
  icons,
  LABELS,
  SEARCH_DATA,
  toastError,
} from "../../../shared/constants";
import Loader from "../../../shared/components/Loader";
import ButtonLoader from "../../../shared/components/ButtonLoader";
import Search from "../../../shared/components/Search";
import SimpleButton from "../../../shared/components/Button/Button";
import Select from "../../../shared/components/Select";
import { Dropdown } from "react-bootstrap";
import Filter from "../../Common/Filter/Filter";
import { DEBOUNCE_VALUE } from "../../../shared/constants/resource";
import useDebounce from "../../../shared/utils/hooks/useDebounce";
import { dateAndTimeConvert } from "../../../shared/helperFunc/dateConvert";
import {
  WhatsAppLogItem,
  useWhatsappLogsExportMutation,
  useWhatsappLogsListQuery,
} from "../../../Features/whatsapp/whatsapp-slice";

const STAGE_NAME_MAP: Record<string, string> = {
  order_creation: "Order Creation",
  order_under_processing: "Order Under Processing",
  delivered_to_store: "Delivered to Customer",
  bill_completion: "Bill Completion",
  production_start: "Production Start",
  cutting_completed: "Cutting Completed",
  embroidery_completed: "Embroidery Completed",
  stitching_completed: "Stitching Completed",
  finishing_completed: "Finishing Completed",
  ready_for_dispatch: "Ready for Dispatch",
  customer_dispatch: "Customer Dispatch",
};

const DELIVERY_STATUS_DROP = [
  { _id: "pending", name: "Pending" },
  { _id: "sent", name: "Sent" },
  { _id: "failed", name: "Failed" },
];

// WhatsApp communication log (TSK-WA-014): read-only list of every outbound
// notification with provider delivery status, retries and export.
const WhatsAppLog = () => {
  const [sort, setSort] = useState<any>(SEARCH_DATA);
  const [count, setCount] = useState<number>();
  const [logList, setLogList] = useState<WhatsAppLogItem[]>([]);
  const [searchKeyword, setSearchKeyword] = useState(sort.search);
  const debouncedSearchKeyword = useDebounce(searchKeyword, DEBOUNCE_VALUE);
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState<any>({});
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  const [filterCalled, setFilterCalled]: any = useState(false);
  const [filterSelectionError, setFilterSelectionError]: any = useState();

  const queryArgs = useMemo(() => {
    return {
      limit: sort.limit,
      skip: sort.skip,
      sort: sort.sort,
      orderNumber: debouncedSearchKeyword || undefined,
      deliveryStatus: appliedFilters.deliveryStatus || undefined,
      dateFrom: appliedFilters.startDate || undefined,
      dateTo: appliedFilters.endDate || undefined,
    };
  }, [debouncedSearchKeyword, sort.limit, sort.skip, sort.sort, appliedFilters]);

  const { data, isSuccess, isFetching } = useWhatsappLogsListQuery(queryArgs, {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });

  const [whatsappLogExport, { isLoading: exportLoading }] =
    useWhatsappLogsExportMutation();

  useEffect(() => {
    if (isSuccess) {
      setLogList(data.list);
      setCount(data.count);
    }
  }, [data, isSuccess]);

  const exportFlow = (exportData: any) => {
    const downloadPath = process.env.REACT_APP_BACKEND_URL + "/uploads/";
    const a = document.createElement("a");
    a.href = downloadPath + exportData.item;
    a.download = exportData.file || exportData.item;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleExport = async (format: "csv" | "xlsx") => {
    const exportResponse = await whatsappLogExport({
      format,
      orderNumber: debouncedSearchKeyword || undefined,
      deliveryStatus: appliedFilters.deliveryStatus || undefined,
      dateFrom: appliedFilters.startDate || undefined,
      dateTo: appliedFilters.endDate || undefined,
    })
      .unwrap()
      .catch((err: any) => {
        toastError(err.data?.errors?.[0]?.msg || "Export failed");
      });
    if (exportResponse && exportResponse?.status) {
      exportFlow(exportResponse);
    }
  };

  const handleApplyFilter = () => {
    setAppliedFilters({ ...filterValues });
    setSort({ ...sort, skip: 0 });
    setShowFilter(false);
  };

  const handleCallFilter = () => {
    setFilterValues({ ...appliedFilters });
    setShowFilter(!showFilter);
  };

  const resetFilter = () => {
    setFilterValues({});
    setAppliedFilters({});
    setSort({ ...sort, skip: 0 });
    setFilterSelectionError("");
    setShowFilter(false);
  };

  const closeFilter = () => {
    setFilterValues({ ...appliedFilters });
    setShowFilter(false);
  };

  const handleSearchTextChange = (e: any) => {
    setSearchKeyword(e.target.value);
    setSort({ ...sort, search: e.target.value, skip: 0 });
  };

  const handleClearSearch = () => {
    setSearchKeyword("");
    setSort({ ...sort, search: "", skip: 0 });
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleOnBlur = (e: any) => {
    const trimmedValue = e.target.value.trim();
    setFilterValues({ ...filterValues, [e.target.name]: trimmedValue });
  };

  const handleStartDate = (date: any) => {
    const fromDate = date
      ? new Date(new Date(date).setHours(0, 0, 0, 0)).toISOString()
      : null;
    setFilterValues((prev: any) => ({ ...prev, startDate: fromDate }));
  };

  const handleEndDate = (date: any) => {
    const toDate = date
      ? new Date(new Date(date).setHours(23, 59, 59, 999)).toISOString()
      : null;
    setFilterValues((prev: any) => ({ ...prev, endDate: toDate }));
  };

  const handleSelect = (item: any, e: any) => {
    if (item?.value) {
      setFilterValues((prev: any) => ({
        ...prev,
        [e.target.name]: item.value,
      }));
    } else {
      setFilterValues((prev: any) => {
        const updated = { ...prev };
        delete updated[e.target.name];
        return updated;
      });
    }
    setFilterSelectionError("");
  };

  const filterContent = (
    handleStartDate: any,
    handleEndDate: any,
    handleSelectStatus: any
  ) => {
    return [
      {
        label: LABELS.START_DATE_TEXT,
        name: ACTION_LABEL.START_DATE,
        elementType: "date",
        action: handleStartDate,
        type: "",
        operator: "is from",
        maxDate: filterValues?.endDate ? new Date(filterValues.endDate) : undefined,
      },
      {
        label: LABELS.END_DATE_TEXT,
        name: ACTION_LABEL.END_DATE,
        elementType: "date",
        action: handleEndDate,
        type: "",
        operator: "is from",
        minDate: filterValues?.startDate ? new Date(filterValues.startDate) : undefined,
      },
      {
        label: LABELS.DELIVERY_STATUS_TEXT,
        name: ACTION_LABEL.DELIVERY_STATUS,
        elementType: "select",
        action: handleSelectStatus,
        data: DELIVERY_STATUS_DROP,
        selectText: "All",
        type: "",
        operator: "is from",
      },
    ];
  };

  const columns = useMemo(
    () => [
      {
        header: LABELS.ORDER_NUMBER_TEXT,
        cell: ({ row }: any) => row.original.orderNumber || "-",
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.CUSTOMER_NAME_TEXT,
        cell: ({ row }: any) => row.original.customerName || "-",
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.CONTACT_NUMBER_TEXT,
        cell: ({ row }: any) => row.original.contactNumber || "-",
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.STAGE_NAME_TEXT,
        cell: ({ row }: any) => {
          const item = row.original;
          return (
            item.stageName ||
            item.stageLabel ||
            (item.stageKey && STAGE_NAME_MAP[item.stageKey]) ||
            item.stageKey ||
            "-"
          );
        },
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.MESSAGE_TYPE_TEXT,
        cell: ({ row }: any) => row.original.messageType || "-",
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.JOB_STATE_TEXT,
        cell: ({ row }: any) => row.original.jobState || "-",
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.DELIVERY_STATUS_TEXT,
        cell: ({ row }: any) => {
          const status = row.original.deliveryStatus;
          return (
            <span className={`badge ${status === "failed" ? "bg-danger" : "bg-success"}`}>
              {status || "-"}
            </span>
          );
        },
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.RETRY_COUNT_TEXT,
        cell: ({ row }: any) => row.original.retryCount ?? 0,
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.CREATED_AT_TEXT,
        cell: ({ row }: any) =>
          row.original.createdAt
            ? dateAndTimeConvert(row.original.createdAt)
            : "-",
        isShow: true,
        enableSorting: false,
      },
    ],
    [sort]
  );

  return (
    <>
      <div className="full-height">
        <Navbar title={LABELS.WHATSAPP_LOG_TEXT} />
        <main className="main-content">
          <div className="pt-3">
            <div className="container-fluid">
              <div className="row table-top-actions">
                <div className="col-12 col-md-12 d-flex justify-content-end">
                  <div className="d-flex align-items-center row w-100 mx-m-0">
                    <Search
                      placeholder={LABELS.ORDER_NUMBER_TEXT}
                      name={ACTION_LABEL.ORDER_NUMBER}
                      value={sort.search}
                      onChange={handleSearchTextChange}
                      onKeyDown={handleKeyDown}
                      onClick={() => {}}
                      onClear={handleClearSearch}
                    />



                    <Dropdown className="custom-dropdown d-flex align-items-center w-auto px-0 me-2">
                      <Dropdown.Toggle
                        as="div"
                        className="d-inline-block cursor-pointer caret-hide"
                      >
                        <SimpleButton
                          className="btn theme-btn theme-btn-sm btn-inverse w-auto"
                          disabled={exportLoading}
                          text={
                            exportLoading ? (
                              <ButtonLoader />
                            ) : (
                              BUTTON_LABEL.EXPORT || "Export"
                            )
                          }
                        >
                          {!exportLoading && (
                            <i
                              className="icon-chevron-down ms-1"
                              style={{ fontSize: "10px" }}
                            ></i>
                          )}
                        </SimpleButton>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleExport("xlsx")}>
                          Excel
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleExport("csv")}>
                          CSV
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                    <SimpleButton
                      className="me-2 btn theme-btn theme-btn-sm btn-inverse filter-btn"
                      btnIcon={icons.ICON_FILTER}
                      btnIconClass={"me-2 d-inline-block"}
                      onClick={handleCallFilter}
                    />
                  </div>
                </div>
              </div>

              <div className="card custom-card card-full mt-3">
                <div className="card-body p-0">
                  <Table
                    data={logList}
                    columns={columns}
                    sort={sort}
                    setSort={setSort}
                    coloumnDrop={false}
                    count={count}
                    isFetching={isFetching}
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <Filter
        sort={filterValues}
        filterSelectionError={filterSelectionError}
        filterObject={filterContent(handleStartDate, handleEndDate, handleSelect)}
        show={showFilter}
        onClose={closeFilter}
        handleReset={resetFilter}
        handleApply={handleApplyFilter}
        handleOnBlur={handleOnBlur}
      />

      {exportLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader />
        </div>
      )}
    </>
  );
};

export default WhatsAppLog;
